import React from 'react'
import { useSelector } from 'react-redux'

import { getChannelLabel } from 'data/channels/helpers'

import { getAllNotes } from 'data/notes/selectors'
import { getChannels } from 'data/channels/selectors'

import ChannelIcon from 'components/Utils/ChannelIcon'
import NoteIcon from 'components/Notes/Icon'

import useAlgoliaSearch from './useAlgoliaSearch'

import { NoteChildrenLoader, ChannelNotesLoader } from '../Loaders'

export default function useEntries({
  query,

  selectedEntryId,
  focusedEntryKey,
  expandedEntryIds,
  onBlur,
  onSelect,
  onToggleExpand,
}) {
  const [entries, setEntries] = React.useState([])

  const channelsFromStore = useSelector(getChannels)
  const notesFromStore = useSelector(getAllNotes)
  const searchResults = useAlgoliaSearch(query)

  const makeEntry = React.useMemo(
    () => {
      return function makeEntry({
        // entry data
        id,
        depth,
        title,
        detail,
        icon,
        isExpandable = false,
        onSelectData,

        //
        isMeta = false,
        renderBypass = undefined,
      }) {
        // same entity can appear several times in the structure (as search result + nested in another search result)
        const key = id + depth

        return {
          id,
          depth,
          key,
          title,
          detail,
          icon,
          isExpandable,
          onSelectData,

          isSelected: selectedEntryId === id,
          isFocused: focusedEntryKey === key,
          isExpanded: expandedEntryIds.has(id),

          onBlur,
          onSelect,
          onToggleExpand,

          isMeta,
          renderBypass,
        }
      }
    },
    // this props are shared by all entries, we'll just spread this bag everywhere
    [
      selectedEntryId,
      focusedEntryKey,
      expandedEntryIds,

      // callbacks
      onBlur,
      onSelect,
      onToggleExpand,
    ]
  )

  const makeNoteEntries = React.useMemo(
    () =>
      function makeNoteEntries({
        depth,
        id,
        hasChildren,
        iconShape,
        iconColor,
        title,
        channelId,
      }) {
        const noteEntry = makeEntry({
          id,
          depth,
          isExpandable: hasChildren,
          icon: (
            <NoteIcon
              iconShape={iconShape}
              iconColor={iconColor}
              hasChildren={hasChildren}
            />
          ),
          title: title || 'Untitled',
          onSelectData: {
            channelId,
            parentNoteId: id,
            title: title || 'Untitled',
          },
        })

        const noteIsExpanded = expandedEntryIds.has(id)
        if (!noteIsExpanded) {
          return [noteEntry]
        }

        const noteChildren = notesFromStore
          .filter(
            note =>
              note.parentNote &&
              note.parentNote.get('id') === id &&
              !note.archivedAt
          )
          .toArray()

        const noteChildrenEntries = noteChildren.reduce(
          (noteChildrenEntries, note) => {
            const {
              id,
              hasChildren,
              iconShape,
              iconColor,
              title,
              channelId,
            } = note

            return noteChildrenEntries.concat(
              makeNoteEntries({
                depth: depth + 1,
                id,
                hasChildren,
                iconShape,
                iconColor,
                title,
                channelId,
              })
            )
          },
          []
        )

        const noteChildrenLoader = makeEntry({
          isMeta: true,
          renderBypass() {
            return <NoteChildrenLoader noteId={id} />
          },
          id: `note-${id}-loader`,
          title: `Note ${id} loader`,
          depth: depth + 1,
        })
        return [noteEntry, ...noteChildrenEntries, noteChildrenLoader]
      },
    [makeEntry, expandedEntryIds, notesFromStore]
  )

  const makeChannelEntries = React.useMemo(
    () => {
      return function({ depth, id, name, type }) {
        const channelEntry = makeEntry({
          id,
          title: getChannelLabel({ type, name }),
          depth: 0,
          icon: <ChannelIcon useNewIcons type={type} />,
          isExpandable: true,
          onSelectData: {
            channelId: id,
            title: name,
          },
        })

        const channelIsExpanded = expandedEntryIds.has(id)
        if (!channelIsExpanded) {
          return [channelEntry]
        }

        const notesForChannel = notesFromStore.filter(
          note => note.channelId === id && !note.archivedAt && !note.parentNote
        )
        const noteEntries = notesForChannel.reduce(function(
          noteEntries,
          { id, hasChildren, iconShape, iconColor, title, channelId }
        ) {
          return noteEntries.concat(
            makeNoteEntries({
              depth: depth + 1,
              id,
              hasChildren,
              iconShape,
              iconColor,
              title,
              channelId,
            })
          )
        },
        [])

        return [channelEntry].concat(noteEntries).concat(
          makeEntry({
            isMeta: true,
            renderBypass() {
              return <ChannelNotesLoader channelId={id} />
            },
            id: `channel-${id}-loader`,
            title: `channel-${id}-loader`,
            depth: depth + 1,
          })
        )
      }
    },
    [makeEntry, makeNoteEntries, notesFromStore, expandedEntryIds]
  )

  const entriesForNoQuery = React.useMemo(
    () => {
      if (query) {
        return [] // we don't show all channel entries when searching for something
      }

      return channelsFromStore
        .sort((a, b) => a.get('name').localeCompare(b.get('name')))
        .toArray()
        .reduce(function(entries, channelRecord) {
          const { id, name, type } = channelRecord
          const channelEntries = makeChannelEntries({
            depth: 0,
            id,
            name,
            type,
          })
          return entries.concat(channelEntries)
        }, [])
    },
    [query, makeChannelEntries, channelsFromStore]
  )

  const entriesForQuery = React.useMemo(
    () => {
      const {
        channels: channelsFromSearch,
        notes: notesFromSearch,
      } = searchResults

      const channelsEntries = channelsFromSearch.reduce(function(
        allChannelEntries,
        channelSearchResult
      ) {
        const { objectID: id, name, type } = channelSearchResult
        return allChannelEntries.concat(
          makeChannelEntries({
            depth: 0,
            id,
            name,
            type,
          })
        )
      },
      [])

      const notesEntries = notesFromSearch.reduce(function(
        allNoteEntries,
        noteSearchResult
      ) {
        const {
          objectID: id,
          fullPath: detail,
          title,
          hasChildren,
          iconShape,
          iconColor,
          channelId,
        } = noteSearchResult
        return allNoteEntries.concat(
          makeNoteEntries({
            depth: 0,
            id,
            hasChildren,
            iconShape,
            iconColor,
            title,
            detail,
            channelId,
          })
        )
      },
      [])

      return channelsEntries.concat(notesEntries)
    },
    [makeChannelEntries, makeNoteEntries, searchResults]
  )

  React.useEffect(
    function makeAllEntries() {
      if (query) {
        setEntries(entriesForQuery)
      } else {
        setEntries(entriesForNoQuery)
      }
    },
    [query, entriesForNoQuery, entriesForQuery]
  )

  const navigableEntryKeys = React.useMemo(
    () => {
      return entries.reduce((navigableEntryKeys, entry) => {
        if (entry.isMeta) {
          return navigableEntryKeys
        }

        return navigableEntryKeys.concat(entry.key)
      }, [])
    },
    [entries]
  )

  return [entries, navigableEntryKeys]
}
